import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthProvider } from "@/auth/AuthContext";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import FarmProfile from "@/pages/FarmProfile";
import Crops from "@/pages/Crops";
import CropDetail from "@/pages/CropDetail";
import HowItWorks from "@/pages/HowItWorks";
import ForFarmers from "@/pages/ForFarmers";
import About from "@/pages/About";
import Onboarding from "@/pages/Onboarding";
import Support from "@/pages/Support";
import { SubjectBilling, PaymentCheckout, PaymentResult, PaymentHistory } from "@/pages/Billing";
import { Login, Register } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import FarmEdit from "@/pages/FarmEdit";
import Atlas from "@/pages/Atlas";
import ProjectDetail from "@/pages/ProjectDetail";
import Projects from "@/pages/Projects";
import OpportunityDetail from '@/pages/OpportunityDetail';
import ForEntities from "@/pages/ForEntities";
import FAQ from "@/pages/FAQ";
import NotFound from "@/pages/NotFound";
import Plans from '@/pages/Plans';
import Legal from '@/pages/Legal';
import PageMeta from "@/components/PageMeta";

const ScrollTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    let target;
    try { target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null; } catch { target = null; }
    if (target) target.scrollIntoView({ block: "start" });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Layout = () => {
  const { pathname } = useLocation();
  const cormorantReading = ["/", "/crops", "/explore", "/atlas"].includes(pathname);
  return (
  <div className="App min-h-screen flex flex-col">
    <Navbar />
    <main className={`flex-1${cormorantReading ? "" : " pa-reading-page"}`}>
      <Outlet />
    </main>
    <Footer />
  </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
        <ScrollTop />
        <PageMeta />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/farm/:id" element={<FarmProfile />} />
            <Route path="/azienda/:slug" element={<FarmProfile />} />
            <Route path="/allevamento/:slug" element={<FarmProfile />} />
            <Route path="/vivaio/:slug" element={<FarmProfile />} />
            <Route path="/professionista/:slug" element={<FarmProfile />} />
            <Route path="/comune/:slug" element={<FarmProfile />} />
            <Route path="/ente/:slug" element={<FarmProfile />} />
            <Route path="/parco/:slug" element={<FarmProfile />} />
            <Route path="/area-protetta/:slug" element={<FarmProfile />} />
            <Route path="/giardino/:slug" element={<FarmProfile />} />
            <Route path="/orto-botanico/:slug" element={<FarmProfile />} />
            <Route path="/oasi/:slug" element={<FarmProfile />} />
            <Route path="/centro-ambientale/:slug" element={<FarmProfile />} />
            <Route path="/associazione/:slug" element={<FarmProfile />} />
            <Route path="/fondazione/:slug" element={<FarmProfile />} />
            <Route path="/soggetto/:slug" element={<FarmProfile />} />
            <Route path="/crops" element={<Crops />} />
            <Route path="/crops/:slug" element={<CropDetail />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/for-farmers" element={<ForFarmers />} />
            <Route path="/for-entities" element={<ForEntities />} />
            <Route path="/about" element={<About />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/cookie" element={<Legal doc="cookie" />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="/subscription-terms" element={<Legal doc="subscription" />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/list-your-farm" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/atlas" element={<Atlas />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/progetto/:slug" element={<ProjectDetail />} />
            <Route path="/projects/:subjectId/:opportunityId" element={<ProjectDetail />} />
            <Route path="/opportunities/:subjectId/:opportunityId" element={<OpportunityDetail />} />
            <Route path="/support" element={<Support />} />
            <Route path="/support/contribute" element={<PaymentCheckout platform />} />
            <Route path="/payments/opportunities/:farmId/:opportunityId" element={<PaymentCheckout />} />
            <Route path="/payments/result" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
            <Route path="/payments/history" element={<ProtectedRoute><PaymentHistory /></ProtectedRoute>} />
            <Route path="/dashboard/farms/:id/billing" element={<ProtectedRoute><SubjectBilling /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/farms/:id/edit" element={<ProtectedRoute><FarmEdit /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;


